import moment from "moment";
import type { DayValue } from "@playerelite/ui";
import { sortedValidDayBaseArray } from "../../constant/attractor-select";
import { KioskAttractor } from "../../types";

export type CampaignStatus = "active" | "future" | "expired";
export type CampaignTab = CampaignStatus | "all";

export const ISO_DATE = "YYYY-MM-DD";

/* ------------------------------------------------------------------ days */

/** The API stores "MON,TUE,…"; the kit's DaySelector speaks "mon", "tue", …. */
export function daysFromVisibility(
  dayVisibility: string | null | undefined
): DayValue[] {
  if (!dayVisibility) return [];
  const wanted = new Set(
    dayVisibility.split(",").map((d) => d.trim().toUpperCase())
  );
  return sortedValidDayBaseArray
    .filter((d) => wanted.has(d))
    .map((d) => d.toLowerCase() as DayValue);
}

/** Always written Monday-first, whatever order the days were ticked in. */
export function visibilityFromDays(days: DayValue[]): string {
  const wanted = new Set(days.map((d) => d.toUpperCase()));
  return sortedValidDayBaseArray.filter((d) => wanted.has(d)).join(",");
}

/* ------------------------------------------------------------------ dates */

export function isoFromEpoch(epoch: number | null | undefined): string {
  return epoch ? moment(epoch).format(ISO_DATE) : "";
}

/**
 * The range picker is date-only, but attractors made on the old page carry a time of day.
 * Keep the saved time when the date itself was not changed, so opening and saving an
 * attractor does not quietly move when it starts or stops.
 */
export function startEpochFromIso(iso: string, savedEpoch?: number): number {
  if (savedEpoch && isoFromEpoch(savedEpoch) === iso) return savedEpoch;
  return moment(iso, ISO_DATE).startOf("day").valueOf();
}

/** A new end date runs to the end of that day, so the last day is shown in full. */
export function endEpochFromIso(iso: string, savedEpoch?: number): number {
  if (savedEpoch && isoFromEpoch(savedEpoch) === iso) return savedEpoch;
  return moment(iso, ISO_DATE).endOf("day").valueOf();
}

export function formatDateRange(startEpoch: number, endEpoch: number): string {
  const display = (epoch: number) => moment(epoch).format("DD/MM/YYYY");
  return `${display(startEpoch)} → ${display(endEpoch)}`;
}

/* ------------------------------------------------------------------ status */

export function campaignStatus(
  attractor: Pick<KioskAttractor, "startDateEpoch" | "endDateEpoch">,
  now: number
): CampaignStatus {
  if (now < attractor.startDateEpoch) return "future";
  if (now > attractor.endDateEpoch) return "expired";
  return "active";
}

/** Whole days until an active attractor ends; 0 on its last day. */
export function daysLeft(endEpoch: number, now: number): number {
  return Math.max(0, moment(endEpoch).diff(moment(now), "days"));
}

const SORTS: Record<
  CampaignTab,
  (a: KioskAttractor, b: KioskAttractor) => number
> = {
  // Ending soonest first — the ones that need attention.
  active: (a, b) => a.endDateEpoch - b.endDateEpoch,
  // Starting soonest first.
  future: (a, b) => a.startDateEpoch - b.startDateEpoch,
  // Most recently ended first.
  expired: (a, b) => b.endDateEpoch - a.endDateEpoch,
  all: (a, b) => b.startDateEpoch - a.startDateEpoch,
};

export function attractorsForTab(
  attractors: KioskAttractor[],
  tab: CampaignTab,
  now: number
): KioskAttractor[] {
  return attractors
    .filter((a) => tab === "all" || campaignStatus(a, now) === tab)
    .sort(SORTS[tab]);
}

/* ------------------------------------------------------------------ filters */

export const CONTENT_FILTER_ID = "contentType";
export const GAMING_ONLY_FILTER = "gaming-only";

/** Any ticked option matches, as in the Storybook design. No selection matches everything. */
export function matchesContentFilter(
  attractor: KioskAttractor,
  selected: string[] | undefined
): boolean {
  if (!selected || selected.length === 0) return true;
  return selected.some((filter) =>
    filter === GAMING_ONLY_FILTER
      ? Boolean(attractor.isGamingContent)
      : attractor.contentType === filter
  );
}

/* ------------------------------------------------------------------ media */

/** The list only gives us a URL, and presigned URLs carry a query string after the extension. */
export const isVideoUrl = (url: string) => /\.mp4($|[?#])/i.test(url);

export const isVideoFile = (file: File) =>
  file.type.startsWith("video/") || /\.mp4$/i.test(file.name);
